import React, { useId, useMemo, useState } from 'react';
import CountryMultiSelect from './CountryMultiSelect';
import type {
    SimulationStrategyCode,
    StrategySimulationArmResult,
    StrategySimulationRecommended,
    StrategySimulationRunResponse,
} from '../types';
import { useStrategySimulatorPanelState } from './strategySimulator/useStrategySimulatorPanelState';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from './ui/table';

const STRATEGY_LABELS: Record<SimulationStrategyCode, string> = {
    embedding_default: 'Embedding default',
    hard_gate: 'Hard gate',
    soft_boost: 'Soft boost',
    default_tiered: 'Default tiered',
    default_legacy: 'Default legacy',
};

const STRATEGY_DESCRIPTIONS: Record<SimulationStrategyCode, string> = {
    embedding_default:
        'Embedding retrieval with traffic re-rank. Uses content similarity to find relevant URLs, then re-ranks by traffic. Evaluated once (not profile-dependent).',
    hard_gate:
        'Same embedding pool as embedding default, but with strict category gating — URLs must match blueprint categories. Evaluated once.',
    soft_boost:
        'Embedding retrieval re-ranked by weighted similarity / category / traffic. Each weight profile shifts the balance between these signals.',
    default_tiered:
        'Category-first tiered flow: primary → secondary → general backfill. Does not use embedding similarity. Evaluated once.',
    default_legacy:
        'Legacy default retrieval implementation. Evaluated once as a regular arm.',
};

interface StrategySimulatorPanelProps {
    promptId: string;
    initialCountryCodes?: string[];
    simulatorEnabled: boolean;
    embedded?: boolean;
}

export default function StrategySimulatorPanel({
    promptId,
    initialCountryCodes = [],
    simulatorEnabled,
    embedded = false,
}: StrategySimulatorPanelProps) {
    const sectionTitleId = useId();
    const [open, setOpen] = useState(false);
    const sim = useStrategySimulatorPanelState(promptId, initialCountryCodes);

    if (!simulatorEnabled) {
        return null;
    }

    if (embedded) {
        return (
            // Embedded = inside the blueprint drawer's canvas band, so the panel
            // carries the same surface shell the funnel drawer uses (page lock 7).
            <section
                className="rounded-xl border border-line bg-surface p-3.5"
                aria-labelledby={sectionTitleId}
            >
                <PanelHeader sectionTitleId={sectionTitleId} />
                <ExpandedPanel promptId={promptId} sim={sim} />
            </section>
        );
    }

    return (
        <section
            className="mt-3 rounded-lg border border-line bg-canvas/80 p-3"
            aria-labelledby={sectionTitleId}
        >
            <PanelHeader sectionTitleId={sectionTitleId} />
            <PanelToggle open={open} onToggle={() => setOpen((o) => !o)} />
            {open && <ExpandedPanel promptId={promptId} sim={sim} />}
        </section>
    );
}

function PanelHeader({ sectionTitleId }: { sectionTitleId: string }) {
    return (
        <>
            <div className="flex flex-wrap items-center gap-2">
                <h3 id={sectionTitleId} className="text-sm font-semibold text-ink">
                    Strategy simulator
                </h3>
                <span className="text-xs font-medium rounded-full border border-warn bg-warn-bg px-2 py-0.5 text-warn">
                    Read-only simulation
                </span>
            </div>
            <p className="mt-1 text-xs text-ink-muted">
                Compares strategies without changing live retrieval. Runs a one-off read-only query; does not
                update your blueprint.
            </p>
            <p className="mt-1 text-xs text-accent">
                Private preview: visible only for marko.dupor@eskimi.com.
            </p>
        </>
    );
}

function PanelToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-all duration-200 ${
                open
                    ? 'border-accent bg-nav-active text-accent-active'
                    : 'border-line bg-surface text-ink-muted hover:bg-ghost-bg'
            }`}
        >
            <ChevronIcon open={open} />
            <span>{open ? 'Hide simulator' : 'Show simulator'}</span>
        </button>
    );
}

function ChevronIcon({ open }: { open: boolean }) {
    return (
        <svg
            className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    );
}

type SimState = ReturnType<typeof useStrategySimulatorPanelState>;

function ExpandedPanel({
    promptId,
    sim,
}: {
    promptId: string;
    sim: SimState;
}) {
    return (
        <div className="mt-3 space-y-4 border-t border-line pt-3">
            <SimulatorFields promptId={promptId} sim={sim} />
            {sim.fieldError && <FieldErrorBanner message={sim.fieldError} />}
            <RunButton polling={sim.polling} onRun={sim.runSimulation} />
            <SimulationProgress sim={sim} />
            <ResultsRegion sim={sim} />
        </div>
    );
}

function FieldErrorBanner({ message }: { message: string }) {
    return (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-800">
            {message}
        </div>
    );
}

function RunButton({ polling, onRun }: { polling: boolean; onRun: () => void }) {
    return (
        <button
            type="button"
            onClick={onRun}
            disabled={polling}
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
            {polling ? 'Running simulation…' : 'Run simulation'}
        </button>
    );
}

function SimulatorFields({
    promptId,
    sim,
}: {
    promptId: string;
    sim: SimState;
}) {
    return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <SampleSizeField promptId={promptId} value={sim.sampleSize} onChange={sim.setSampleSize} />
            <HorizonField promptId={promptId} value={sim.horizonDays} onChange={sim.setHorizonDays} />
            <div className="md:col-span-2">
                <span className="mb-1 block text-sm font-medium text-ink">Country codes</span>
                <CountryMultiSelect
                    value={sim.countryCodes}
                    onChange={sim.setCountryCodes}
                    placeholder="Default: blueprint / server resolution"
                    disabled={sim.polling}
                />
                <p className="mt-1 text-xs text-ink-muted">Leave empty to use blueprint defaults.</p>
            </div>
            <ExcludeLast24HoursField
                promptId={promptId}
                checked={sim.excludeLast24Hours}
                disabled={sim.polling}
                onChange={sim.setExcludeLast24Hours}
            />
        </div>
    );
}

function ExcludeLast24HoursField({
    promptId,
    checked,
    disabled,
    onChange,
}: {
    promptId: string;
    checked: boolean;
    disabled: boolean;
    onChange: (next: boolean) => void;
}) {
    return (
        <div className="md:col-span-2">
            <label htmlFor={`${promptId}-sim-exclude-24h`} className="inline-flex items-center gap-2 text-sm text-ink">
                <input
                    id={`${promptId}-sim-exclude-24h`}
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={(ev) => onChange(ev.target.checked)}
                    className="h-4 w-4 rounded border-line text-primary-600 focus:ring-primary-500"
                />
                Exclude URLs scanned in the last 24 hours
            </label>
        </div>
    );
}

function SampleSizeField({
    promptId,
    value,
    onChange,
}: {
    promptId: string;
    value: number;
    onChange: (n: number) => void;
}) {
    return (
        <div>
            <label htmlFor={`${promptId}-sim-sample`} className="block text-sm font-medium text-ink">
                Sample size
            </label>
            <input
                id={`${promptId}-sim-sample`}
                type="number"
                min={200}
                max={5000}
                value={value}
                onChange={(ev) => onChange(Number(ev.target.value))}
                className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                aria-describedby={`${promptId}-sim-sample-hint`}
            />
            <p id={`${promptId}-sim-sample-hint`} className="mt-1 text-xs text-ink-muted">
                Between 200 and 5000 URLs.
            </p>
        </div>
    );
}

function HorizonField({
    promptId,
    value,
    onChange,
}: {
    promptId: string;
    value: number;
    onChange: (n: number) => void;
}) {
    return (
        <div>
            <label htmlFor={`${promptId}-sim-horizon`} className="block text-sm font-medium text-ink">
                Traffic horizon (days)
            </label>
            <input
                id={`${promptId}-sim-horizon`}
                type="number"
                min={1}
                max={7}
                value={value}
                onChange={(ev) => onChange(Number(ev.target.value))}
                className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                aria-describedby={`${promptId}-sim-horizon-hint`}
            />
            <p id={`${promptId}-sim-horizon-hint`} className="mt-1 text-xs text-ink-muted">
                Between 1 and 7 days.
            </p>
        </div>
    );
}

function SimulationProgress({ sim }: { sim: SimState }) {
    if (!sim.polling) return null;
    const pct = Math.min(100, Math.max(0, sim.overallPercent));
    const rounded = Math.round(pct);
    return (
        <div className="space-y-1.5">
            <ProgressStatusLine sim={sim} rounded={rounded} />
            <div
                className="h-2.5 w-full overflow-hidden rounded-full bg-line"
                role="progressbar"
                aria-valuenow={rounded}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Simulation progress ${rounded}%`}
            >
                <div
                    className="h-2.5 rounded-full bg-primary-500 transition-[width] duration-500 ease-out"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function ProgressStatusLine({ sim, rounded }: { sim: SimState; rounded: number }) {
    return (
        <div className="flex items-center justify-between text-xs text-ink-muted">
            <span>
                Step {sim.completedProfiles + 1}/{sim.totalProfiles}
                {sim.activeProfile ? ` · ${sim.activeProfile}` : ''}
                {sim.urlScanProgress ? (
                    <span className="ml-1 tabular-nums text-ink-muted">
                        ({sim.urlScanProgress.urlsScanned.toLocaleString()} /{' '}
                        {sim.urlScanProgress.urlsTarget.toLocaleString()} URLs)
                    </span>
                ) : null}
            </span>
            <span className="tabular-nums font-medium text-primary-700">{rounded}%</span>
        </div>
    );
}

function ResultsRegion({ sim }: { sim: SimState }) {
    const showCompletedTable = Boolean(sim.runResult && !sim.polling);
    const showEmptyHint =
        !sim.polling &&
        !sim.runResult &&
        !sim.runError;

    return (
        <div aria-live="polite" className="space-y-3">
            {sim.runError && (
                <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-800">
                    {sim.runError}
                </div>
            )}
            {showEmptyHint && (
                <p className="text-sm text-ink-muted">Run a simulation to see results.</p>
            )}
            {showCompletedTable && sim.runResult && <CompletedRun run={sim.runResult} />}
        </div>
    );
}

function RecommendationCard({
    title,
    subtitle,
    rec,
    scoreMin,
    scoreMax,
}: {
    title: string;
    subtitle: string;
    rec: StrategySimulationRecommended;
    scoreMin: number;
    scoreMax: number;
}) {
    return (
        <div className="rounded-xl border border-line bg-canvas p-3">
            <h4 className="text-sm font-semibold text-ink">{title}</h4>
            <p className="mt-1 text-xs text-ink-muted">{subtitle}</p>
            <RecommendationDetails rec={rec} scoreMin={scoreMin} scoreMax={scoreMax} />
        </div>
    );
}

function RecommendationSection({
    recommended,
    scoreMin,
    scoreMax,
    cardSubtitle = 'Based on deterministic strategy evaluation.',
}: {
    recommended: StrategySimulationRecommended | null;
    scoreMin: number;
    scoreMax: number;
    cardSubtitle?: string;
}) {
    return (
        <div className="space-y-2">
            {!recommended ? (
                <NoEligibleRecommendation />
            ) : (
                <RecommendationCard
                    title="Recommendation"
                    subtitle={cardSubtitle}
                    rec={recommended}
                    scoreMin={scoreMin}
                    scoreMax={scoreMax}
                />
            )}
        </div>
    );
}

function RunRecommendationSection({
    run,
    scoreMin,
    scoreMax,
    cardSubtitle,
}: {
    run: StrategySimulationRunResponse;
    scoreMin: number;
    scoreMax: number;
    cardSubtitle?: string;
}) {
    return (
        <RecommendationSection
            recommended={run.recommended ?? null}
            scoreMin={scoreMin}
            scoreMax={scoreMax}
            cardSubtitle={cardSubtitle}
        />
    );
}

function CompletedRun({
    run,
    recommendationSubtitle,
}: {
    run: StrategySimulationRunResponse;
    recommendationSubtitle?: string;
}) {
    return (
        <>
            <CompletedRunTables run={run} recommendationSubtitle={recommendationSubtitle} />
        </>
    );
}

function CompletedRunTables({
    run,
    recommendationSubtitle,
}: {
    run: StrategySimulationRunResponse;
    recommendationSubtitle?: string;
}) {
    const results = run.results ?? [];
    const { min: scoreMin, max: scoreMax } = scoreRangeFromResults(results);
    return (
        <>
            <RunRecommendationSection
                run={run}
                scoreMin={scoreMin}
                scoreMax={scoreMax}
                cardSubtitle={recommendationSubtitle}
            />
            <ResultsTable
                results={results}
                scoreMin={scoreMin}
                scoreMax={scoreMax}
            />
        </>
    );
}

function scoreRangeFromResults(results: StrategySimulationArmResult[]): { min: number; max: number } {
    const scores = results
        .filter((r) => r.status === 'completed' && Number.isFinite(r.score))
        .map((r) => r.score);
    if (scores.length === 0) {
        return { min: 0, max: 1 };
    }
    return { min: Math.min(...scores), max: Math.max(...scores) };
}

function NoEligibleRecommendation() {
    return (
        <div className="rounded-md border-l-4 border-warn bg-warn-bg p-3 text-sm text-warn">
            <p className="font-medium">No eligible strategy for this run.</p>
            <p className="mt-1 text-xs text-warn">
                Common causes: underfill below gates, traffic/category thresholds not met, or per-arm
                timeouts/failures.
            </p>
        </div>
    );
}

function RecommendationDetails({
    rec,
    scoreMin,
    scoreMax,
}: {
    rec: StrategySimulationRecommended;
    scoreMin: number;
    scoreMax: number;
}) {
    const conf = formatRecommendedConfidence(rec.confidence);
    return (
        <dl className="mt-2 grid grid-cols-1 gap-1 text-sm text-ink sm:grid-cols-2">
            <div>
                <dt className="text-ink-muted">Strategy</dt>
                <dd className="font-medium">{formatRecommendedStrategyLine(rec)}</dd>
            </div>
            <div>
                <dt className="text-ink-muted">Score</dt>
                <dd className="tabular-nums">
                    <SimulationScoreSpan score={rec.score} scoreMin={scoreMin} scoreMax={scoreMax} />
                </dd>
            </div>
            <div>
                <dt className="text-ink-muted">Eligible</dt>
                <dd>{rec.eligible ? 'Yes' : 'No'}</dd>
            </div>
            {conf && (
                <div>
                    <dt className="text-ink-muted">Confidence</dt>
                    <dd className="tabular-nums">{conf}</dd>
                </div>
            )}
        </dl>
    );
}

function formatRecommendedConfidence(confidence: number | undefined): string | null {
    if (confidence === undefined) {
        return null;
    }
    return confidence <= 1 ? `${Math.round(confidence * 100)}%` : `${confidence}%`;
}

function formatRecommendedStrategyLine(rec: StrategySimulationRecommended): string {
    const code = rec.strategy as SimulationStrategyCode;
    const label = STRATEGY_LABELS[code] ?? rec.strategy;
    if (rec.profile) {
        return `${label} — ${formatWeightProfileDisplay(rec.profile)}`;
    }
    return label;
}

function formatWeightProfileDisplay(profile: string): string {
    return profile
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

type SortDirection = 'asc' | 'desc';
type TableSortKey =
    | 'strategy'
    | 'status'
    | 'fillRate'
    | 'uniqueCount'
    | 'categoryHitRate'
    | 'avgSimilarity'
    | 'avgTraffic'
    | 'score'
    | 'eligibility';

interface TableColumn {
    key: TableSortKey;
    label: string;
    description: string;
    align: 'left' | 'right';
    sticky?: boolean;
}

const TABLE_COLUMNS: TableColumn[] = [
    {
        key: 'strategy',
        label: 'Strategy',
        description:
            'Retrieval arm for this row. Soft boost rows include the weight profile in the same cell (e.g. Soft boost — Traffic heavy).',
        align: 'left',
        sticky: true,
    },
    {
        key: 'status',
        label: 'Status',
        description: 'Execution outcome for this arm (completed, timeout, or failed).',
        align: 'left',
    },
    {
        key: 'fillRate',
        label: 'Fill %',
        description: 'selectedCount / requestedCount for this arm.',
        align: 'right',
    },
    {
        key: 'uniqueCount',
        label: 'Unique',
        description: 'Count of distinct URLs in selected output.',
        align: 'right',
    },
    {
        key: 'categoryHitRate',
        label: 'Category hit %',
        description: 'Share of selected URLs matching blueprint primary/secondary DSP categories.',
        align: 'right',
    },
    {
        key: 'avgSimilarity',
        label: 'Avg similarity',
        description: 'Average embedding similarity for selected URLs (N/A when not applicable).',
        align: 'right',
    },
    {
        key: 'avgTraffic',
        label: 'Avg traffic',
        description: 'Average traffic/impression signal of selected URLs over the chosen horizon.',
        align: 'right',
    },
    {
        key: 'score',
        label: 'Score',
        description:
            'Deterministic composite score used for ranking recommendations. Hue runs red (weaker in this comparison) to green (stronger) using min/max across completed rows in this table.',
        align: 'right',
    },
];

const ELIGIBILITY_COLUMN: TableColumn = {
    key: 'eligibility',
    label: 'Eligibility',
    description: 'Eligible when there are no reject reason codes; otherwise shows failure codes.',
    align: 'left',
};

const REJECT_REASON_DESCRIPTIONS: Record<string, string> = {
    FAIL_TIMEOUT: 'The strategy exceeded the simulation time budget.',
    FAIL_ARM_FAILED: 'This strategy arm failed during evaluation.',
    FAIL_NO_EMBEDDING: 'No valid embedding was available for this blueprint.',
    FAIL_FILL_HARD: 'Fill rate was below the hard minimum threshold (80%).',
    FAIL_FILL_AUTO_PICK: 'Result is below the auto-pick safety fill threshold.',
    FAIL_FILL_COUNT: 'Selected URL count was below the minimum required count.',
    FAIL_TRAFFIC_RETENTION: 'Traffic retention fell below the 65% threshold.',
    FAIL_CATEGORY_UPLIFT: 'Category hit rate did not satisfy the required uplift threshold.',
};

function ResultsTable({
    results,
    scoreMin,
    scoreMax,
}: {
    results: StrategySimulationArmResult[];
    scoreMin: number;
    scoreMax: number;
}) {
    const [sortBy, setSortBy] = useState<{ key: TableSortKey; direction: SortDirection } | null>(null);
    const sortedResults = useMemo(() => sortResultsForDisplay(results, sortBy), [results, sortBy]);
    return (
        <div className="overflow-x-auto overflow-y-visible rounded-md border border-line bg-surface">
            <Table>
                <ResultsTableHead
                    sortBy={sortBy}
                    onSort={(key) => setSortBy((prev) => nextTableSort(prev, key))}
                />
                <TableBody>
                    {sortedResults.map((r, idx) => (
                        <ResultRow
                            key={`${r.strategy}-${r.profile ?? 'shared'}`}
                            row={r}
                            rowIndex={idx}
                            rowCount={sortedResults.length}
                            scoreMin={scoreMin}
                            scoreMax={scoreMax}
                        />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function ResultsTableHead({
    sortBy,
    onSort,
}: {
    sortBy: { key: TableSortKey; direction: SortDirection } | null;
    onSort: (key: TableSortKey) => void;
}) {
    return (
        <TableHead className="relative z-30 bg-canvas">
            <tr>
                {TABLE_COLUMNS.map((col) => {
                    const activeDirection = sortBy?.key === col.key ? sortBy.direction : null;
                    const align =
                        col.sticky === true
                            ? 'sticky left-0 z-10 bg-canvas text-left'
                            : col.align === 'right'
                              ? 'text-right'
                              : 'text-left';
                    return (
                        <TableHeaderCell
                            key={col.label}
                            scope="col"
                            aria-sort={
                                activeDirection === 'asc'
                                    ? 'ascending'
                                    : activeDirection === 'desc'
                                      ? 'descending'
                                      : 'none'
                            }
                            className={`relative z-30 ${align}`}
                        >
                            <HeaderLabelWithTooltip
                                label={col.label}
                                description={col.description}
                                alignLeftTooltip={col.sticky === true}
                                sortDirection={activeDirection}
                                onSort={() => onSort(col.key)}
                            />
                        </TableHeaderCell>
                    );
                })}
                <TableHeaderCell
                    scope="col"
                    aria-sort={
                        sortBy?.key === ELIGIBILITY_COLUMN.key
                            ? sortBy.direction === 'asc'
                                ? 'ascending'
                                : 'descending'
                            : 'none'
                    }
                    className="relative z-30 min-w-[12rem] text-left"
                >
                    <HeaderLabelWithTooltip
                        label={ELIGIBILITY_COLUMN.label}
                        description={ELIGIBILITY_COLUMN.description}
                        alignLeftTooltip
                        sortDirection={sortBy?.key === ELIGIBILITY_COLUMN.key ? sortBy.direction : null}
                        onSort={() => onSort(ELIGIBILITY_COLUMN.key)}
                    />
                </TableHeaderCell>
            </tr>
        </TableHead>
    );
}

function HeaderLabelWithTooltip({
    label,
    description,
    alignLeftTooltip,
    sortDirection,
    onSort,
}: {
    label: string;
    description: string;
    alignLeftTooltip: boolean;
    sortDirection: SortDirection | null;
    onSort: () => void;
}) {
    const tooltipPositionClass = alignLeftTooltip
        ? 'left-0 -translate-x-0'
        : 'left-1/2 -translate-x-1/2';
    return (
        <span className="group relative inline-flex items-center gap-1">
            <button
                type="button"
                onClick={onSort}
                className="inline-flex items-center gap-1 rounded-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-500 focus:ring-offset-1"
                title="Click to sort"
            >
                {label}
                <span aria-hidden className="text-xs text-ink-muted">
                    {sortDirection === 'asc' ? '▲' : sortDirection === 'desc' ? '▼' : '↕'}
                </span>
            </button>
            <span
                aria-hidden
                className={`pointer-events-none absolute top-full z-50 mt-1 hidden w-56 rounded-md bg-black px-2 py-1 text-left text-xs font-normal text-white shadow-lg group-hover:block ${tooltipPositionClass}`}
            >
                {description}
            </span>
        </span>
    );
}

function SimulationScoreSpan({
    score,
    scoreMin: _scoreMin,
    scoreMax: _scoreMax,
}: {
    score: number;
    scoreMin: number;
    scoreMax: number;
}) {
    return (
        <span>
            {score.toFixed(4)}
        </span>
    );
}

function ResultRow({
    row: r,
    rowIndex,
    rowCount,
    scoreMin,
    scoreMax,
}: {
    row: StrategySimulationArmResult;
    rowIndex: number;
    rowCount: number;
    scoreMin: number;
    scoreMax: number;
}) {
    const done = r.status === 'completed';
    return (
        <TableRow aria-label={resultRowAriaLabel(r)} className="even:bg-ghost-bg/80">
            <StrategySummaryCell row={r} rowIndex={rowIndex} rowCount={rowCount} />
            <TableCell className="whitespace-nowrap">
                <StatusBadge status={r.status} />
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {done ? formatPct(r.fillRate) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">{done ? r.uniqueCount : '—'}</TableCell>
            <TableCell className="text-right tabular-nums">
                {done ? formatPct(r.categoryHitRate) : '—'}
            </TableCell>
            <SimilarityMetricCell r={r} done={done} />
            <TableCell className="text-right tabular-nums">
                {done ? formatAvgTraffic(r.avgTraffic) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {done ? (
                    <SimulationScoreSpan score={r.score} scoreMin={scoreMin} scoreMax={scoreMax} />
                ) : (
                    '—'
                )}
            </TableCell>
            <TableCell className="min-w-[12rem] text-xs align-middle">
                <EligibilityCell reasons={r.rejectReasons} />
            </TableCell>
        </TableRow>
    );
}

function EligibilityCell({ reasons }: { reasons?: string[] }) {
    const hasReasons = Boolean(reasons?.length);
    const reasonList = reasons ?? [];
    return (
        <div className="grid grid-cols-[1.1rem_minmax(0,1fr)] items-stretch gap-x-2">
            <div
                className="flex h-full items-center text-base leading-none"
                role="img"
                aria-label={hasReasons ? 'Not eligible' : 'Eligible'}
                title={hasReasons ? 'Not eligible' : 'Eligible'}
            >
                {hasReasons ? '❌' : '✅'}
            </div>
            <div className="min-h-[1rem] min-w-0">
                {hasReasons ? (
                    <div className="flex flex-col items-start gap-1">
                        {reasonList.map((reason) => (
                            <RejectReasonBadge key={reason} reason={reason} />
                        ))}
                    </div>
                ) : (
                    <span aria-hidden />
                )}
            </div>
        </div>
    );
}

function RejectReasonBadge({ reason }: { reason: string }) {
    const description = REJECT_REASON_DESCRIPTIONS[reason] ?? 'No description available for this reason code yet.';
    return (
        <span className="group relative inline-flex max-w-full items-center rounded border border-line bg-canvas px-1.5 py-0.5 text-xs text-ink">
            <span tabIndex={0} className="cursor-help break-all rounded-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                {reason}
            </span>
            <span className="pointer-events-none absolute bottom-full right-0 z-50 mb-1 hidden w-64 rounded-md bg-black px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block">
                {description}
            </span>
        </span>
    );
}

function resultRowAriaLabel(r: StrategySimulationArmResult): string {
    const base = STRATEGY_LABELS[r.strategy];
    if (r.strategy === 'soft_boost' && r.profile) {
        return `${base}, ${formatWeightProfileDisplay(r.profile)} profile`;
    }
    return base;
}

function strategySummaryPrimaryText(r: StrategySimulationArmResult): string {
    if (r.strategy === 'soft_boost' && r.profile) {
        return `${STRATEGY_LABELS.soft_boost} — ${formatWeightProfileDisplay(r.profile)}`;
    }
    return STRATEGY_LABELS[r.strategy];
}

function StrategySummaryCell({
    row,
    rowIndex,
    rowCount,
}: {
    row: StrategySimulationArmResult;
    rowIndex: number;
    rowCount: number;
}) {
    const description = STRATEGY_DESCRIPTIONS[row.strategy];
    const showAbove = rowIndex >= Math.max(2, rowCount - 2);
    const tooltipPositionClass = showAbove ? 'bottom-full mb-1' : 'top-full mt-1';
    return (
        <TableCell className="sticky left-0 z-10 bg-surface even:bg-ghost-bg/80 hover:z-40 focus-within:z-40">
            <span className="group relative inline-flex items-center font-medium text-ink">
                <span
                    tabIndex={0}
                    className="cursor-help rounded-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:ring-offset-1"
                >
                    {strategySummaryPrimaryText(row)}
                </span>
                {description && (
                    <span
                        aria-hidden
                        className={`pointer-events-none absolute left-0 z-50 hidden w-64 rounded-md bg-black px-2 py-1.5 text-xs font-normal text-white shadow-lg group-hover:block ${tooltipPositionClass}`}
                    >
                        {description}
                    </span>
                )}
            </span>
        </TableCell>
    );
}

function SimilarityMetricCell({ r, done }: { r: StrategySimulationArmResult; done: boolean }) {
    let inner: React.ReactNode = '—';
    if (done) {
        inner =
            r.similarityNotApplicable || r.avgSimilarity == null ? (
                <span aria-label="N/A">N/A</span>
            ) : (
                r.avgSimilarity.toFixed(3)
            );
    }
    return <TableCell className="text-right tabular-nums">{inner}</TableCell>;
}

function StatusBadge({ status }: { status: StrategySimulationArmResult['status'] }) {
    const styles =
        status === 'completed'
            ? 'bg-safe-bg text-safe border-safe'
            : status === 'timeout'
              ? 'bg-warn-bg text-warn border-warn'
              : 'bg-risk-bg text-risk border-risk';
    return (
        <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${styles}`}>
            {status}
        </span>
    );
}

function formatPct(n: number): string {
    return `${(n * 100).toFixed(1)}%`;
}

function formatConfidencePct(n: number): string {
    const normalized = n <= 1 ? n * 100 : n;
    return `${normalized.toFixed(1)}%`;
}

function formatAvgTraffic(n: number): string {
    if (!Number.isFinite(n)) {
        return '—';
    }
    return n.toFixed(2);
}

function nextTableSort(
    previous: { key: TableSortKey; direction: SortDirection } | null,
    key: TableSortKey
): { key: TableSortKey; direction: SortDirection } {
    if (!previous || previous.key !== key) {
        return { key, direction: 'asc' };
    }
    return { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' };
}

function sortResultsForDisplay(
    rows: StrategySimulationArmResult[],
    sortBy: { key: TableSortKey; direction: SortDirection } | null
): StrategySimulationArmResult[] {
    if (!sortBy) {
        return rows;
    }
    return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => compareRowsForSort(a.row, b.row, sortBy) || a.index - b.index)
        .map((item) => item.row);
}

function compareRowsForSort(
    a: StrategySimulationArmResult,
    b: StrategySimulationArmResult,
    sortBy: { key: TableSortKey; direction: SortDirection }
): number {
    const left = sortValueForKey(a, sortBy.key);
    const right = sortValueForKey(b, sortBy.key);
    const cmp = compareSortValues(left, right);
    return sortBy.direction === 'asc' ? cmp : -cmp;
}

function compareSortValues(a: number | string | null, b: number | string | null): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }
    return String(a).localeCompare(String(b));
}

function sortValueForKey(r: StrategySimulationArmResult, key: TableSortKey): number | string | null {
    if (key === 'strategy') return strategySummaryPrimaryText(r);
    if (key === 'status') return sortStatusOrder(r.status);
    if (key === 'fillRate') return r.status === 'completed' ? r.fillRate : null;
    if (key === 'uniqueCount') return r.status === 'completed' ? r.uniqueCount : null;
    if (key === 'categoryHitRate') return r.status === 'completed' ? r.categoryHitRate : null;
    if (key === 'avgSimilarity') return similaritySortValue(r);
    if (key === 'avgTraffic') return r.status === 'completed' ? r.avgTraffic : null;
    if (key === 'score') return r.status === 'completed' ? r.score : null;
    return (r.rejectReasons?.length ?? 0) === 0 ? 1 : 0;
}

function similaritySortValue(r: StrategySimulationArmResult): number | null {
    if (r.status !== 'completed') return null;
    if (r.similarityNotApplicable || r.avgSimilarity == null) return null;
    return r.avgSimilarity;
}

function sortStatusOrder(status: StrategySimulationArmResult['status']): number {
    if (status === 'completed') return 0;
    if (status === 'timeout') return 1;
    return 2;
}
